import React, { useEffect, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Image,
  Input,
  Link,
  Select,
  SimpleGrid,
  Stack,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  Textarea,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import axiosInstance from '../../../api/AxiosInstance';
import { fetchLocalidadesCatalogo, LocalidadOption } from '../../../utils/localidadesCatalogo';

interface Props {
  modo: 'new' | 'edit' | 'view';
  data?: any;
  onGuardado?: () => void;
  onCancelar?: () => void;
}

type FormState = {
  nombres: string;
  apellidos: string;
  curp: string;
  rfc: string;
  direccion: string;
  localidad: string;
  municipio: string;
  telefono: string;
  correo: string;
  tipoArtesania: string;
  tecnica: string;
  descripcion: string;
  experienciaAnios: string;
  capacidadProd: string;
  temporadaInicio: string;
  temporadaFin: string;
  temporadaDetalle: string;
  estatus: string;
};

const initialForm: FormState = {
  nombres: '',
  apellidos: '',
  curp: '',
  rfc: '',
  direccion: '',
  localidad: '',
  municipio: '',
  telefono: '',
  correo: '',
  tipoArtesania: '',
  tecnica: '',
  descripcion: '',
  experienciaAnios: '',
  capacidadProd: '',
  temporadaInicio: '',
  temporadaFin: '',
  temporadaDetalle: '',
  estatus: 'activo',
};

const parseTemporada = (value: string | null | undefined) => {
  const raw = (value || '').trim();
  if (!raw) return { temporadaInicio: '', temporadaFin: '', temporadaDetalle: '' };

  const [base, detalle = ''] = raw.split('|').map((p) => p.trim());
  const match = base.match(/^(\d{4}-\d{2}-\d{2})\s+a\s+(\d{4}-\d{2}-\d{2})$/i);
  if (match) {
    return {
      temporadaInicio: match[1],
      temporadaFin: match[2],
      temporadaDetalle: detalle,
    };
  }

  return { temporadaInicio: '', temporadaFin: '', temporadaDetalle: raw };
};

const buildTemporada = (form: FormState) => {
  const parts: string[] = [];
  if (form.temporadaInicio && form.temporadaFin) {
    parts.push(`${form.temporadaInicio} a ${form.temporadaFin}`);
  }
  if (form.temporadaDetalle.trim()) parts.push(form.temporadaDetalle.trim());
  return parts.join(' | ');
};

const getBaseOrigin = () => {
  const configured = axiosInstance.defaults.baseURL as string | undefined;
  if (!configured) return window.location.origin;
  try {
    return new URL(configured, window.location.origin).origin;
  } catch {
    return window.location.origin;
  }
};

const resolveAssetUrl = (value: string) => {
  if (!value) return '';
  if (/^https?:\/\//i.test(value)) return value;
  return `${getBaseOrigin()}${value.startsWith('/') ? '' : '/'}${value}`;
};

const isImagePath = (value: string) => /\.(png|jpe?g|webp|gif)$/i.test(value);

const FormularioArtesano: React.FC<Props> = ({ modo, data, onGuardado, onCancelar }) => {
  const [form, setForm] = useState<FormState>(initialForm);
  const [fotosExistentes, setFotosExistentes] = useState<string[]>([]);
  const [fotosNuevas, setFotosNuevas] = useState<File[]>([]);
  const [ineActual, setIneActual] = useState<string>('');
  const [ineNueva, setIneNueva] = useState<File | null>(null);
  const [localidades, setLocalidades] = useState<LocalidadOption[]>([]);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  const disabled = modo === 'view';

  useEffect(() => {
    fetchLocalidadesCatalogo().then(setLocalidades).catch(() => setLocalidades([]));
  }, []);

  useEffect(() => {
    if (!data) {
      setForm(initialForm);
      setFotosExistentes([]);
      setFotosNuevas([]);
      setIneActual('');
      setIneNueva(null);
      return;
    }

    const temporada = parseTemporada(data.temporadaVenta);
    setForm({
      nombres: data.nombres || '',
      apellidos: data.apellidos || '',
      curp: data.curp || '',
      rfc: data.rfc || '',
      direccion: data.direccion || '',
      localidad: data.localidad || '',
      municipio: data.municipio || '',
      telefono: data.telefono || '',
      correo: data.correo || '',
      tipoArtesania: data.tipoArtesania || '',
      tecnica: data.tecnica || '',
      descripcion: data.descripcion || '',
      experienciaAnios:
        data.experienciaAnios === null || data.experienciaAnios === undefined
          ? ''
          : String(data.experienciaAnios),
      capacidadProd: data.capacidadProd || '',
      estatus: data.estatus || 'activo',
      ...temporada,
    });

    setFotosExistentes(Array.isArray(data.fotos) ? data.fotos.filter(Boolean) : []);
    setFotosNuevas([]);
    setIneActual(data.ineUrl || '');
    setIneNueva(null);
  }, [data]);

  const handleChange = (field: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleFotosSeleccionadas = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setFotosNuevas((prev) => [...prev, ...files]);
    e.target.value = '';
  };

  const handleIneSeleccionada = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setIneNueva(file);
  };

  const removeFotoNueva = (index: number) => {
    setFotosNuevas((prev) => prev.filter((_, i) => i !== index));
  };

  const removeFotoExistente = (url: string) => {
    setFotosExistentes((prev) => prev.filter((f) => f !== url));
  };

  const quitarIneActual = () => {
    setIneActual('');
  };

  const handleSubmit = async () => {
    if (!form.nombres.trim() || !form.apellidos.trim()) {
      setError('Nombres y apellidos son obligatorios.');
      return;
    }

    setError('');
    setGuardando(true);
    try {
      const temporadaVenta = buildTemporada(form);
      const fd = new FormData();

      [
        ['nombres', form.nombres],
        ['apellidos', form.apellidos],
        ['curp', form.curp],
        ['rfc', form.rfc],
        ['direccion', form.direccion],
        ['localidad', form.localidad],
        ['municipio', form.municipio],
        ['telefono', form.telefono],
        ['correo', form.correo],
        ['tipoArtesania', form.tipoArtesania],
        ['tecnica', form.tecnica],
        ['descripcion', form.descripcion],
        ['experienciaAnios', form.experienciaAnios],
        ['capacidadProd', form.capacidadProd],
        ['temporadaVenta', temporadaVenta],
        ['estatus', form.estatus],
      ].forEach(([key, value]) => fd.append(key, value ?? ''));

      fd.append('fotosExistentes', JSON.stringify(fotosExistentes));
      fd.append('ineActual', ineActual);
      fotosNuevas.forEach((file) => fd.append('fotos', file));
      if (ineNueva) fd.append('identificacionIne', ineNueva);

      if (modo === 'edit' && data?.id) {
        await axiosInstance.put(`/turismo/artesanos/${data.id}`, fd);
      } else {
        await axiosInstance.post('/turismo/artesanos', fd);
      }

      onGuardado && onGuardado();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'No fue posible guardar el artesano.');
    } finally {
      setGuardando(false);
    }
  };

  const temporadaPreview = buildTemporada(form);

  return (
    <Box>
      <Stack spacing={5}>
        <Box borderWidth="1px" borderRadius="lg" p={4} bg="gray.50">
          <HStack justify="space-between" mb={3}>
            <Text fontWeight="bold" color="#5b182b">Datos personales</Text>
            <Badge colorScheme={form.estatus === 'activo' ? 'green' : 'gray'}>{form.estatus || 'activo'}</Badge>
          </HStack>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <FormControl isRequired>
              <FormLabel>Nombres</FormLabel>
              <Input value={form.nombres} onChange={(e) => handleChange('nombres', e.target.value)} isDisabled={disabled} />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Apellidos</FormLabel>
              <Input value={form.apellidos} onChange={(e) => handleChange('apellidos', e.target.value)} isDisabled={disabled} />
            </FormControl>
            <FormControl>
              <FormLabel>CURP</FormLabel>
              <Input value={form.curp} onChange={(e) => handleChange('curp', e.target.value.toUpperCase())} isDisabled={disabled} />
            </FormControl>
            <FormControl>
              <FormLabel>RFC</FormLabel>
              <Input value={form.rfc} onChange={(e) => handleChange('rfc', e.target.value.toUpperCase())} isDisabled={disabled} />
            </FormControl>
            <FormControl>
              <FormLabel>Teléfono</FormLabel>
              <Input type="tel" value={form.telefono} onChange={(e) => handleChange('telefono', e.target.value)} isDisabled={disabled} />
            </FormControl>
            <FormControl>
              <FormLabel>Correo</FormLabel>
              <Input type="email" value={form.correo} onChange={(e) => handleChange('correo', e.target.value)} isDisabled={disabled} />
            </FormControl>
            <FormControl>
              <FormLabel>Localidad</FormLabel>
              <Select value={form.localidad} onChange={(e) => handleChange('localidad', e.target.value)} isDisabled={disabled}>
                <option value="">Selecciona localidad</option>
                {localidades.map((loc) => (
                  <option key={loc.id} value={loc.label}>{loc.label}</option>
                ))}
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel>Municipio</FormLabel>
              <Input value={form.municipio} onChange={(e) => handleChange('municipio', e.target.value)} isDisabled={disabled} />
            </FormControl>
            <FormControl gridColumn={{ md: '1 / -1' }}>
              <FormLabel>Dirección</FormLabel>
              <Textarea value={form.direccion} onChange={(e) => handleChange('direccion', e.target.value)} isDisabled={disabled} rows={2} />
            </FormControl>
          </SimpleGrid>
        </Box>

        <Box borderWidth="1px" borderRadius="lg" p={4}>
          <Text fontWeight="bold" color="#5b182b" mb={3}>Producción y comercialización</Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <FormControl>
              <FormLabel>Tipo de artesanía</FormLabel>
              <Input value={form.tipoArtesania} onChange={(e) => handleChange('tipoArtesania', e.target.value)} isDisabled={disabled} placeholder="Ej. Textil, barro, madera" />
            </FormControl>
            <FormControl>
              <FormLabel>Técnica</FormLabel>
              <Input value={form.tecnica} onChange={(e) => handleChange('tecnica', e.target.value)} isDisabled={disabled} placeholder="Ej. Telar de cintura" />
            </FormControl>
            <FormControl>
              <FormLabel>Experiencia (años)</FormLabel>
              <Input type="number" min={0} value={form.experienciaAnios} onChange={(e) => handleChange('experienciaAnios', e.target.value)} isDisabled={disabled} />
            </FormControl>
            <FormControl>
              <FormLabel>Capacidad de producción</FormLabel>
              <Input value={form.capacidadProd} onChange={(e) => handleChange('capacidadProd', e.target.value)} isDisabled={disabled} placeholder="Ej. 30 piezas por semana" />
            </FormControl>
            <FormControl>
              <FormLabel>Temporada de venta (inicio)</FormLabel>
              <Input type="date" value={form.temporadaInicio} onChange={(e) => handleChange('temporadaInicio', e.target.value)} isDisabled={disabled} />
            </FormControl>
            <FormControl>
              <FormLabel>Temporada de venta (fin)</FormLabel>
              <Input type="date" value={form.temporadaFin} onChange={(e) => handleChange('temporadaFin', e.target.value)} isDisabled={disabled} />
            </FormControl>
            <FormControl gridColumn={{ md: '1 / -1' }}>
              <FormLabel>Detalle de temporada / observaciones comerciales</FormLabel>
              <Textarea
                value={form.temporadaDetalle}
                onChange={(e) => handleChange('temporadaDetalle', e.target.value)}
                isDisabled={disabled}
                rows={2}
                placeholder="Ej. Ventas altas en ferias patronales y temporada decembrina"
              />
              {temporadaPreview && (
                <FormHelperText>
                  Se guardará como: <strong>{temporadaPreview}</strong>
                </FormHelperText>
              )}
            </FormControl>
            <FormControl gridColumn={{ md: '1 / -1' }}>
              <FormLabel>Descripción del trabajo artesanal</FormLabel>
              <Textarea value={form.descripcion} onChange={(e) => handleChange('descripcion', e.target.value)} isDisabled={disabled} rows={4} />
            </FormControl>
          </SimpleGrid>
        </Box>

        <Box borderWidth="1px" borderRadius="lg" p={4} bg="gray.50">
          <Text fontWeight="bold" color="#5b182b" mb={3}>Evidencias y documentos (archivo)</Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <FormControl>
              <FormLabel>Estatus</FormLabel>
              <Select value={form.estatus} onChange={(e) => handleChange('estatus', e.target.value)} isDisabled={disabled}>
                <option value="activo">Activo</option>
                <option value="inactivo">Inactivo</option>
              </Select>
            </FormControl>
            {!disabled && (
              <FormControl>
                <FormLabel>Subir INE / identificación</FormLabel>
                <Input type="file" accept=".pdf,image/*" onChange={handleIneSeleccionada} p={1} />
                <FormHelperText>PDF o imagen.</FormHelperText>
              </FormControl>
            )}
          </SimpleGrid>

          <Box mt={4}>
            <Text fontSize="sm" fontWeight="semibold" mb={2}>Identificación registrada</Text>
            {ineActual ? (
              <HStack wrap="wrap" spacing={2}>
                <Tag bg="#f7efe8" color="#5b182b" borderRadius="full">
                  <TagLabel>
                    <Link href={resolveAssetUrl(ineActual)} isExternal>
                      {ineActual.split('/').pop()}
                    </Link>
                  </TagLabel>
                  {!disabled && <TagCloseButton onClick={quitarIneActual} />}
                </Tag>
              </HStack>
            ) : (
              <Text color="gray.500" fontSize="sm">Sin identificación registrada.</Text>
            )}
          </Box>

          {ineNueva && (
            <Box mt={3}>
              <Text fontSize="sm" fontWeight="semibold" mb={2}>Nuevo archivo INE por subir</Text>
              <Tag colorScheme="blue" borderRadius="full">
                <TagLabel>{ineNueva.name}</TagLabel>
                {!disabled && <TagCloseButton onClick={() => setIneNueva(null)} />}
              </Tag>
            </Box>
          )}

          <Divider my={4} />

          {!disabled && (
            <FormControl mb={4}>
              <FormLabel>Fotos del artesano / taller</FormLabel>
              <Input type="file" accept="image/*" multiple onChange={handleFotosSeleccionadas} p={1} />
              <FormHelperText>Sube evidencia fotográfica en lugar de URLs.</FormHelperText>
            </FormControl>
          )}

          <Stack spacing={3}>
            <Box>
              <Text fontSize="sm" fontWeight="semibold" mb={2}>Fotos registradas</Text>
              {fotosExistentes.length === 0 ? (
                <Text color="gray.500" fontSize="sm">Sin fotos registradas.</Text>
              ) : (
                <Wrap>
                  {fotosExistentes.map((foto) => (
                    <WrapItem key={foto}>
                      <Tag bg="#f7efe8" color="#5b182b" borderRadius="full">
                        <TagLabel>
                          <Link href={resolveAssetUrl(foto)} isExternal>
                            {foto.split('/').pop()}
                          </Link>
                        </TagLabel>
                        {!disabled && <TagCloseButton onClick={() => removeFotoExistente(foto)} />}
                      </Tag>
                    </WrapItem>
                  ))}
                </Wrap>
              )}
            </Box>

            {fotosExistentes.filter(isImagePath).length > 0 && (
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                {fotosExistentes.filter(isImagePath).map((foto) => (
                  <Box key={`${foto}-preview`} borderWidth="1px" borderRadius="md" overflow="hidden">
                    <Image src={resolveAssetUrl(foto)} alt={foto} h="120px" w="100%" objectFit="cover" />
                  </Box>
                ))}
              </SimpleGrid>
            )}

            <Divider />

            <Box>
              <Text fontSize="sm" fontWeight="semibold" mb={2}>Nuevas fotos por cargar</Text>
              {fotosNuevas.length === 0 ? (
                <Text color="gray.500" fontSize="sm">No has seleccionado archivos nuevos.</Text>
              ) : (
                <Wrap>
                  {fotosNuevas.map((file, index) => (
                    <WrapItem key={`${file.name}-${index}`}>
                      <Tag colorScheme="blue" borderRadius="full">
                        <TagLabel>{file.name}</TagLabel>
                        {!disabled && <TagCloseButton onClick={() => removeFotoNueva(index)} />}
                      </Tag>
                    </WrapItem>
                  ))}
                </Wrap>
              )}
            </Box>
          </Stack>
        </Box>

        {error && (
          <Box borderWidth="1px" borderColor="red.200" bg="red.50" borderRadius="md" p={3}>
            <Text color="red.600" fontSize="sm">{error}</Text>
          </Box>
        )}
      </Stack>

      {modo !== 'view' && (
        <Box mt={6} display="flex" gap={3} justifyContent="flex-end">
          <Button onClick={onCancelar} variant="outline">Cancelar</Button>
          <Button bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={handleSubmit} isLoading={guardando} loadingText="Guardando">
            Guardar
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default FormularioArtesano;
